import { TypographyP2Bold } from '@heidi/design-system/typography';
import { logger } from '@heidi/runtime/logger';
import { cn } from '@heidi/utils';
import * as m from 'motion/react-m';
import {
  useEffect,
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { DictationFeedbackContext } from '../../../contracts/dictation-feedback.types';
import { getDictationAnalyticsPorts } from '../../../dictation-analytics-ports';
import { bubbleEnterExitAnimation } from '../../shared/animation';
import { DictationCloseButton } from '../../shared/components/dictation-close-button';
import { DictationResultContent } from '../../shared/components/dictation-result-content';

/** What the prompt hands the app's shared thumbs button for each choice. */
export interface WidgetThumbsButtonProps {
  variant: 'up' | 'down';
  selected: boolean;
  size: number;
  type: 'button';
  'aria-label': string;
  disabled: boolean;
  onClick: () => void;
}

interface WidgetOutputFeedbackProps {
  closeVisible: boolean;
  dictationId: string | null;
  isCopied: boolean;
  showFeedback: boolean;
  isSubmitting?: boolean;
  /** Which surface renders the prompt, tagged on the feedback payload. */
  platform: 'web' | 'desktop';
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  onCopy: () => void;
  onMouseEnter: MouseEventHandler<HTMLDivElement>;
  onMouseMove: MouseEventHandler<HTMLDivElement>;
  onMouseLeave: MouseEventHandler<HTMLDivElement>;
  onSubmitFeedback?: (
    feedback: DictationFeedbackContext & { score: 'positive' }
  ) => void | Promise<void>;
  onRequestNegativeFeedback?: (context: DictationFeedbackContext) => void;
  /** The app's shared CSAT thumbs button, filled at each mount. */
  renderThumbsButton: (props: WidgetThumbsButtonProps) => ReactNode;
  /**
   * Positioning for the feedback root. The desktop widget window positions the
   * bubble itself, so it passes nothing; the web bubble supplies the fixed
   * bottom-center placement its other states use.
   */
  className?: string;
}

/** The first, compact screen in the post-dictation feedback pathway. */
const WidgetOutputFeedbackBase = ({
  closeVisible,
  dictationId,
  isCopied,
  showFeedback,
  isSubmitting = false,
  platform,
  onClose,
  onCopy,
  onMouseEnter,
  onMouseMove,
  onMouseLeave,
  onSubmitFeedback,
  onRequestNegativeFeedback,
  renderThumbsButton,
  className,
}: WidgetOutputFeedbackProps) => {
  const intl = useIntl();
  const positiveLabel = intl.formatMessage({
    defaultMessage: 'Good output',
    id: 'cHEKdcKA52',
    description: 'Accessible label for positive dictation feedback',
  });
  const negativeLabel = intl.formatMessage({
    defaultMessage: 'Poor output',
    id: '3+5L8K4pb0',
    description: 'Accessible label for negative dictation feedback',
  });

  useEffect(() => {
    if (!showFeedback) return;

    try {
      getDictationAnalyticsPorts().feedback.trackPromptViewed();
    } catch (error) {
      logger.warn('[dictation/feedback] interaction analytics failed', {
        classification: 'analytics_failed',
        interaction: 'prompt_viewed',
        errorName: error instanceof Error ? error.name : 'UnknownError',
      });
    }
  }, [showFeedback]);

  if (!showFeedback) {
    return (
      <m.div
        key="result"
        data-testid="widget-result"
        className="group relative"
        onMouseEnter={onMouseEnter}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        {...bubbleEnterExitAnimation}
      >
        <DictationResultContent isCopied={isCopied} onCopy={onCopy} />
        <DictationCloseButton onClose={onClose} visible={closeVisible} />
      </m.div>
    );
  }

  return (
    <m.div
      key="feedback"
      data-testid="widget-feedback"
      data-widget-hit-surface
      className={cn('group relative', className)}
      onMouseEnter={onMouseEnter}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      {...bubbleEnterExitAnimation}
    >
      <div
        data-testid="widget-feedback-surface"
        className="flex h-9 w-[214px] items-center gap-1 rounded-2xl bg-surface-accent/80 py-2 pe-3 ps-4 ring-1 ring-inset ring-border backdrop-blur-sm newBrandDark:bg-surface-elevated/80"
      >
        <TypographyP2Bold className="whitespace-nowrap text-text-secondary">
          <FormattedMessage
            defaultMessage="How was the output?"
            id="chQh6ibTsg"
            description="Question asking the user to rate the result of a dictation"
          />
        </TypographyP2Bold>
        {/*
          Thumbs stay selected={false} with no post-click confirmation for now;
          the acknowledgement/thank-you state lands with the feedback-submission
          wiring, alongside the analytics-backed handlers.
        */}
        <div className="flex items-center">
          {renderThumbsButton({
            variant: 'up',
            selected: false,
            size: 12,
            type: 'button',
            'aria-label': positiveLabel,
            disabled: isSubmitting || !onSubmitFeedback,
            onClick: () => {
              void Promise.resolve(
                onSubmitFeedback?.({
                  score: 'positive',
                  dictationId,
                  platform,
                })
              ).catch(() => undefined);
            },
          })}
          {renderThumbsButton({
            variant: 'down',
            selected: false,
            size: 12,
            type: 'button',
            'aria-label': negativeLabel,
            disabled: isSubmitting || !onSubmitFeedback,
            onClick: () => onRequestNegativeFeedback?.({ dictationId, platform }),
          })}
        </div>
      </div>
      <DictationCloseButton overlapCorner onClose={onClose} visible={closeVisible} />
    </m.div>
  );
};

/** The output and rating prompt the desktop widget and the web bubble mount. */
export const WidgetOutputFeedback = WidgetOutputFeedbackBase;
